// Parser de texto de DANFE NFSe extraido via pdf.js. Os PDFs de NFSe
// que vimos nao tem XML embarcado (verificado nos 4 exemplos: Navegantes,
// Itapoa, Curitiba/DANFSe v1.0, Joinville/NF-em), entao a estrategia eh
// regex sobre o texto bruto.
//
// Cada municipio tem layout proprio (tabular vs labelado), entao o parser
// detecta a origem via marcadores no texto e usa estrategias diferentes
// para os campos. Retorna NfseData mesmo se só conseguir extrair parte
// dos campos - quem chama decide o que fazer com lacunas.

import { extractProcesso } from './regex-processo';

export interface Retencoes {
  ir: number;
  pis: number;
  cofins: number;
  csll: number;
  inss: number;
  iss: number;
}

export interface NfseData {
  cnpjEmissor?: string;     // 14 digitos sem formatacao
  nomeEmissor?: string;
  cnpjTomador?: string;     // 14 digitos sem formatacao - identifica a filial Ascensus
  // Todos os CNPJs validos (DV conferido) impressos no documento, na ordem de
  // leitura e sem repeticao. Preenchido pelo pdf-texto para qualquer layout.
  // Serve ao tomadorEntreFiliais quando o rotulo do tomador nao e confiavel
  // (DACTE com rotulos e valores em blocos separados).
  cnpjsDocumento?: string[];
  numeroNf?: string;
  serie?: string;
  // Chave de acesso do CT-e (44 digitos) - extraida do DACTE. Vazia quando
  // nao e CTe ou o parser nao localizou/validou. serie e numeroNf do CTe sao
  // derivados dela quando presente (ver cte-parser).
  chaveAcesso?: string;
  valorTotal?: number;
  cnaeServico?: string;     // codigo de tributacao do servico (varia formato)
  descricaoServico?: string;
  processo?: string;        // "SIGLA NNNN/AA"
  retencoes: Retencoes;
  municipio?: string;
  // Pedagio (CTe rodoviario): F1_VALPEDG. Backend deduz do total do produto
  // e grava separado pra calculo correto de base de credito ICMS conforme TES.
  valorPedagio?: number;
  // Simples Nacional do emitente (CTe). Detectado pelo cte-parser via texto
  // do PDF ("Simples Nacional" presente). Usado pra filtrar TES de regime
  // SIMPLES na planilha REGRAS CTE.
  simplesNacional?: boolean;
  // ICMS isento no CTe (CST 40/41/51 na "Situacao Tributaria" do DACTE).
  // Detectado pelo cte-parser via texto do PDF. Usado pra o autoPickTesCte
  // preferir a TES ISENTO (003/004) na inclusao manual.
  icmsIsento?: boolean;
  // Origem da deteccao - util para debug. 'generic' = fallback.
  // 'cte-*' indica CTe (transporte) extraido pelo cte-parser.
  source: 'danfse2' | 'curitiba' | 'navegantes' | 'itapoa' | 'joinville' | 'itajai' | 'saopaulo' | 'cte' | 'generic';
}

const EMPTY_RETENCOES: Retencoes = { ir: 0, pis: 0, cofins: 0, csll: 0, inss: 0, iss: 0 };

// --- Detectores de municipio ---------------------------------------------

function detectMunicipio(text: string): NfseData['source'] {
  // Remove acentuacao pra detector funcionar tanto com "SAO PAULO" como
  // "SÃO PAULO", "ITAJAI" como "ITAJAÍ", etc. PDFs vem com ambos.
  const upper = text.toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  // 'curitiba' aqui = parser do layout DANFSe v1.0 / TOTVS transmite.
  // Esse layout segue o padrao ABRASF nacional e eh usado por varios
  // municipios (Curitiba, Rio de Janeiro, Sao Jose dos Pinhais, etc.).
  // Detectamos pelo rodape transmite.totvs.app ou pelo header DANFSe v1.0.
  if (upper.includes('TRANSMITE.TOTVS.APP') ||
      upper.includes('DANFSE V1.0') ||
      upper.includes('CURITIBA - PR') ||
      (upper.includes('CURITIBA') && upper.includes('PREFEITURA')) ||
      (upper.includes('CURITIBA') && upper.includes('NFS-E'))) return 'curitiba';
  if (upper.includes('NAVEGANTES') || upper.includes('PORTONAVE')) return 'navegantes';
  if (upper.includes('ITAPOA')) return 'itapoa';
  // Sao Paulo - layout proprio com prestador antes do tomador (linear)
  if (upper.includes('PREFEITURA') && upper.includes('SAO PAULO')) return 'saopaulo';
  // Itajai - layout 2 colunas Publica (mesmo da Joinville mas headers diferentes)
  if (upper.includes('ITAJAI')) return 'itajai';
  if (upper.includes('JOINVILLE') && (upper.includes('NF-EM') || upper.includes('NOTA FISCAL ELETR'))) return 'joinville';
  return 'generic';
}

// --- Helpers de prestador vs tomador (layout 2-colunas Publica) ----------

// Retorna TODOS os CNPJs do texto, na ordem em que aparecem.
function extractAllCnpjs(text: string): string[] {
  const matches = text.match(/\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/g);
  if (!matches) return [];
  return matches.map(m => m.replace(/\D/g, ''));
}

// Detecta layout 2-colunas comum em PDFs do Publica (Joinville/Itajai/etc).
// O texto extraido pelo pdf.js tem o cabecalho "PRESTADOR ... TOMADOR" lado
// a lado (gap pequeno), e depois os dados intercalando linhas das 2 colunas.
// Quando isso acontece, o TOMADOR aparece PRIMEIRO no texto (ele eh a coluna
// da esquerda); o PRESTADOR vem depois.
function isPublicaTwoColumn(text: string): boolean {
  const posP = text.search(/PRESTADOR\s+DE\s+SERVI/i);
  const posT = text.search(/TOMADOR\s+DE\s+SERVI/i);
  if (posP < 0 || posT < 0) return false;
  // Headers proximos = layout 2 colunas
  return Math.abs(posT - posP) < 100;
}

// Resolve o CNPJ do emissor (prestador) considerando o layout.
// - 2 colunas Publica: 1o CNPJ eh do tomador -> pega o 2o
// - linear (SP, ABRASF): pega CNPJ entre PRESTADOR e TOMADOR
// - fallback: 1o CNPJ
function extractEmissorCnpj(text: string): string {
  const cnpjs = extractAllCnpjs(text);

  if (cnpjs.length > 0) {
    if (isPublicaTwoColumn(text)) {
      // Layout 2 colunas: tomador vem 1o, prestador 2o
      return cnpjs[1] || cnpjs[0];
    }

    // Layout linear: CNPJ na regiao do prestador (entre PRESTADOR e TOMADOR)
    const posP = text.search(/PRESTADOR\s+DE\s+SERVI/i);
    const posT = text.search(/TOMADOR\s+DE\s+SERVI/i);
    if (posP >= 0 && posT > posP) {
      const region = text.substring(posP, posT);
      const first = region.match(/\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/);
      if (first) { return first[0].replace(/\D/g, ''); }
    }
    return cnpjs[0];
  }

  // Sem CNPJ formatado: layout Navegantes/Portonave traz o CNPJ do
  // emitente sem formatacao apos o label "CNPJ:" (o 1o "CNPJ:" do doc eh
  // o do prestador; o do tomador vem como "CPF/CNPJ" mais abaixo).
  // Ancorado no label pra nao capturar a chave de acesso (44 digitos).
  const u = text.match(/CNPJ\s*:?\s*(\d{14})(?!\d)/i);
  return u ? u[1] : '';
}

// Resolve o nome do emissor. Estrategia:
// - 2 colunas Publica: linha "<Nome>Nome empresarial:" ou "<Nome>Nome:"
//   marcada como prestador (NOTA: "Nome:" eh tomador, "Nome empresarial:"
//   eh prestador no Publica).
// - linear (SP/ABRASF): primeira linha em CAPS entre PRESTADOR e TOMADOR
//   contendo LTDA/S\\A/EIRELI/ME/EPP.
function extractEmissorNome(text: string): string {
  if (isPublicaTwoColumn(text)) {
    // No layout Publica o nome do prestador vem coladinho ao label
    // "Nome empresarial:" - ex.: "C8 SERVICOS LTDANome empresarial:"
    const m = text.match(/([A-Z0-9][^\n]{3,100}?)Nome\s+empresarial\s*:/i);
    if (m) return m[1].trim();
  }
  const posP = text.search(/PRESTADOR\s+DE\s+SERVI/i);
  const posT = text.search(/TOMADOR\s+DE\s+SERVI/i);
  if (posP >= 0 && posT > posP) {
    const region = text.substring(posP, posT);
    const lines = region.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    for (const line of lines) {
      if (/^[A-Z0-9\s\/\-\.&]+(LTDA|S\/A|S\.A\.?|EIRELI|ME|EPP)/i.test(line)) {
        return line.trim();
      }
    }
  }
  // Fallback: procura nas primeiras 20 linhas
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0).slice(0, 20);
  for (const line of lines) {
    if (/^[A-Z0-9\s\/\-\.&]+(LTDA|S\/A|S\.A\.?|EIRELI|ME|EPP)/i.test(line)) {
      return line.trim();
    }
  }
  return '';
}

// --- Utilitarios ---------------------------------------------------------

// Le numero monetario em formato BR ("1.234,56") OU US ("763.65", "11250.00").
// Heuristicas:
//   - tem virgula -> BR: pontos sao milhares, virgula eh decimal
//   - so periodos + um unico periodo + 2 chars apos -> US decimal
//   - varios periodos OU 3 chars apos o unico periodo -> BR milhares (sem decimal)
// Ignora "R$" e espacos. Retorna 0 se a string nao tiver digitos.
export function parseMoney(s: string): number {
  if (!s) return 0;
  const clean = s.replace(/R\$/gi, '').replace(/\s/g, '').trim();
  if (!/\d/.test(clean)) return 0;
  let normalized: string;
  if (clean.includes(',')) {
    // BR: 14.675,76 -> 14675.76 | 763,65 -> 763.65
    normalized = clean.replace(/\./g, '').replace(',', '.');
  } else {
    const periodCount = (clean.match(/\./g) || []).length;
    if (periodCount === 1) {
      const idx = clean.indexOf('.');
      const after = clean.length - idx - 1;
      // 2 chars apos ponto = US decimal (ex.: 763.65, 11250.00)
      // outros casos = BR milhares (ex.: 14.675 -> 14675)
      normalized = (after === 2) ? clean : clean.replace(/\./g, '');
    } else {
      // varios periodos: BR milhares (ex.: 1.234.567 -> 1234567)
      normalized = clean.replace(/\./g, '');
    }
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

// Alias legacy - mantemos por compatibilidade com codigo que ja chama
// parseMoneyBR. Nao usar em codigo novo.
function parseMoneyBR(s: string): number {
  return parseMoney(s);
}

function extractFirstCnpj(text: string): string {
  // Captura primeiro CNPJ no formato XX.XXX.XXX/XXXX-XX e devolve so digitos.
  const m = text.match(/\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/);
  return m ? m[0].replace(/\D/g, '') : '';
}

// CNPJ do tomador: primeiro CNPJ que aparece apos a secao "TOMADOR".
// Identifica qual filial Ascensus eh a destinataria da NF, usado para
// auto-selecionar a filial no front. Aceita variacoes: "TOMADOR DA NFS-e",
// "TOMADOR DO SERVICO", "TOMADOR DE SERVICOS".
function extractTomadorCnpj(text: string): string {
  const m = text.match(/TOMADOR[\s\S]{0,300}?(\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})/i);
  if (m) { return m[1].replace(/\D/g, ''); }
  // Layout Navegantes/Portonave: CNPJ nao-formatado (14 digitos) apos o
  // label "CPF/CNPJ" dentro do bloco TOMADOR. Ancorado no label pra nao
  // capturar telefone/identificador/chave de acesso (44 digitos).
  const u = text.match(/TOMADOR[\s\S]{0,400}?CPF\/CNPJ[\s\S]{0,20}?(\d{14})(?!\d)/i);
  return u ? u[1] : '';
}

// Procura um valor monetario na primeira linha apos uma label conhecida.
// Funciona para layouts tabulares onde header e values estao em linhas
// separadas alinhadas.
function findValueAfterLabel(lines: string[], labelRegex: RegExp, lookahead = 3): number {
  for (let i = 0; i < lines.length; i++) {
    if (labelRegex.test(lines[i])) {
      // procura nas proximas N linhas o primeiro numero. SKIPA linhas que
      // contem '%' - sao percentuais (aliquota), nao valor monetario.
      // Cenario: PDF Itajai (FRETE INTERNACIONAL) extrai como
      //   Valor IRRF
      //   3,00 %      <-- aliquota ISS na coluna seguinte do PDF
      //   0,00        <-- IRRF real
      // O regex de digitos pegava "3,00" e devolvia 3 como IR (regra
      // cliente 2026-05-29 - pode haver aliquota destacada com valor 0).
      for (let j = 1; j <= lookahead && i + j < lines.length; j++) {
        const line = lines[i + j];
        if (/%/.test(line)) continue;
        const m = line.match(/-?\d[\d.,]*/);
        if (m) return parseMoneyBR(m[0]);
      }
    }
  }
  return 0;
}

// Captura valor monetario apos uma label. Tenta primeiro na MESMA linha
// (layout pdftotext / PDFs cujo label+valor ficam no mesmo y); se vier
// vazia/dash/sem numero, tenta a PROXIMA linha (layout pdf.js, que separa
// items em y distintos -> label e valor saem em linhas diferentes).
// Para evitar capturar valor de label adjacente, so olha a proxima linha
// imediata (nao varias adiante).
function findInlineValue(text: string, labelRegex: RegExp): number {
  const m = text.match(labelRegex);
  if (!m) return 0;
  const start = m.index! + m[0].length;
  const newlineAt = text.indexOf('\n', start);

  // Same-line tentativa. Skipa quando contem '%' (eh aliquota, nao
  // valor monetario - vide nota em findValueAfterLabel).
  if (newlineAt !== -1) {
    const sameLine = text.substring(start, newlineAt);
    if (!/^\s*-+\s*$/.test(sameLine) && !/%/.test(sameLine)) {
      const v = sameLine.match(/R?\$?\s*([\d.,]+)/);
      if (v) return parseMoneyBR(v[1]);
    }
  } else {
    const tail = text.substring(start);
    if (!/^\s*-+\s*$/.test(tail) && !/%/.test(tail)) {
      const v = tail.match(/R?\$?\s*([\d.,]+)/);
      if (v) return parseMoneyBR(v[1]);
    }
    return 0;
  }

  // Next-line tentativa (pdf.js separa label de valor). Skipa quando
  // contem '%' (eh aliquota).
  const nextNl = text.indexOf('\n', newlineAt + 1);
  const nextLineEnd = nextNl === -1 ? text.length : nextNl;
  const nextLine = text.substring(newlineAt + 1, nextLineEnd).trim();
  if (!nextLine || /^-+$/.test(nextLine) || /%/.test(nextLine)) return 0;
  const v2 = nextLine.match(/^R?\$?\s*([\d.,]+)/);
  return v2 ? parseMoneyBR(v2[1]) : 0;
}

// Retencoes FEDERAIS (IR/INSS/PIS/COFINS/CSLL) do corpo do DANFSe.
// Compartilhado por parseCuritiba e parseGeneric: os rotulos sao os mesmos
// em praticamente todos os layouts municipais - muda so' o arranjo visual,
// e o findInlineValue ja cobre "label e valor na mesma linha" e "valor na
// linha de baixo". Ate 2026-08-28 o parseGeneric devolvia SEMPRE zero aqui
// (EMPTY_RETENCOES fixo), entao toda NFS-e de municipio nao mapeado entrava
// no Protheus sem retencao nenhuma - chamado 00017975 (EXACTA / Foz do
// Iguacu, DANFSe que imprime IR 16,14 + PIS 6,99 + COFINS 32,28 + CSLL
// 10,76 e foi lancada com tudo zerado).
//
// Importante: pdf.js separa label e valor em linhas distintas (cada item
// em sua propria coordenada y). Labels como "PIS", "COFINS", "CSLL", "CP"
// ficam em linhas isoladas - usamos ancora de linha (^|\n) + opcional
// whitespace de indentacao. O findInlineValue ja faz fallback pra
// proxima linha quando a atual nao tem o numero.
// IRRF: o rotulo varia por municipio dentro deste mesmo layout.
//   "IRRF"    - transmite / DANFSe v1.0 (maioria)
//   "IRPJ"    - alguns municipios usam o nome do tributo
//   "IR (R$)" - plataforma gestaoiss (Foz do Iguacu e congeneres): o bloco
//               "TRIBUTOS FEDERAIS" rotula cada tributo com o sufixo da
//               unidade. Sem esta variante o IR saia zerado mesmo com o
//               PDF trazendo 16,14.
//   "IR"      - Araucaria/PR (NF 10099): o bloco de totais traz o rotulo
//               isolado "IR" entre "ISSRF" e "INSS". Com /IRRF\b/ a nota
//               vinha com PIS/COFINS/CSLL corretos e IR zerado, e o
//               U_PnfNatFin classificava 253026 (so PCC) em vez de 253016
//               (IRRF 1,5 + PCC) - sem retencao de IRRF no titulo.
// Ordem importa: "IRRF" primeiro preserva exatamente o comportamento
// anterior. "IR" solto so casa LINHA INTEIRA (^IR$) para nao pegar "IR"
// dentro de outra palavra nem o "IR" de "IRRF"/"IRPJ" ja testados acima.
function extractRetencoesFederais(text: string): Omit<Retencoes, 'iss'> {
  let ir = findInlineValue(text, /IRRF\b/i);
  if (ir === 0) {
    ir = findInlineValue(text, /(?:^|\n)\s*IRPJ\b/i);
  }
  if (ir === 0) {
    ir = findInlineValue(text, /(?:^|\n)\s*IR\s*\(\s*R\$\s*\)/i);
  }
  if (ir === 0) {
    ir = findInlineValue(text, /(?:^|\n)[ \t]*IR[ \t]*(?=\n)/);
  }

  // INSS: "CP" (Contrib Previdenciaria) no transmite, "Contribuicao
  // Previdenciaria - Retida" no DANFSe v1.0, "INSS (R$)" no gestaoiss.
  let inss = findInlineValue(text, /(?:^|\n)\s*CP\b/);
  if (inss === 0) {
    inss = findInlineValue(text, /Contribui[çc][aã]o\s+Previdenci[aá]ria\s*-?\s*Retida/i);
  }
  if (inss === 0) {
    inss = findInlineValue(text, /(?:^|\n)\s*INSS\s*\(\s*R\$\s*\)/i);
  }

  // PIS/COFINS/CSLL individuais. Ancora no inicio de linha pra nao capturar
  // PIS dentro de "PIS/COFINS Retidos" ou "PIS - Debito". O sufixo "(R$)"
  // do gestaoiss ja e tolerado: o findInlineValue pula a sobra da linha sem
  // numero e le a linha seguinte.
  let pis    = findInlineValue(text, /(?:^|\n)\s*PIS\b(?!\/|\s*-)/);
  let cofins = findInlineValue(text, /(?:^|\n)\s*COFINS\b(?!\s*-)/);
  let csll   = findInlineValue(text, /(?:^|\n)\s*CSLL\b/);

  // Fallback: se todos individuais zerados, tenta "Contribuicoes Sociais Retidas"
  // (DANFSe v1.0) e split por ratio 0.65/3/1.
  if (pis === 0 && cofins === 0 && csll === 0) {
    const pccTotal = findInlineValue(text, /Contribui[çc][oõ]es\s+Sociais\s*-?\s*Retidas/i);
    if (pccTotal > 0) {
      pis    = +(pccTotal * 0.65 / 4.65).toFixed(2);
      cofins = +(pccTotal * 3.00 / 4.65).toFixed(2);
      csll   = +(pccTotal * 1.00 / 4.65).toFixed(2);
    }
  }

  return { ir, pis, cofins, csll, inss };
}

// Numero da NFS-e lido da CHAVE DE ACESSO NACIONAL (50 digitos) impressa no
// rodape do DANFSe. E a fonte AUTORITATIVA do numero: a chave e o
// identificador assinado do documento e carrega o nNFSe em posicao fixa.
// Layout da chave (NT SE/CGNFS-e 007/2026):
//   cLocIncid(7) ambGer(1) tpInsc(1) nInsc(14) nNFSe(13) AAMM(4) cDF(9) DV(1)
// Sem isto, layouts que imprimem apenas "RPS numero N" no cabecalho (a
// plataforma gestaoiss - Foz do Iguacu e congeneres - nao imprime o numero
// da NFS-e em lugar nenhum) entravam no Protheus com o numero do RPS: a
// NFS-e 2026000002621 da EXACTA virou o documento 000003311. Chamado
// 00017975.
// Devolve o numero CHEIO (13 digitos). O backend normaliza para F1_DOC com
// PadL(Right(numero, 9), 9, '0') - ver notasAdicionar:cadastrarUm.
// Nao afeta CTe: o DACTE e desviado para o cte-parser antes de chegar aqui.
function extractNumeroChaveNacional(text: string): string {
  const m = text.match(/Chave\s+de\s+Acesso[^\n:]*:?\s*([\d\s.\-]{50,90})/i);
  if (!m) { return ''; }
  const dig = m[1].replace(/\D/g, '');
  // Exatamente 50 = chave da NFS-e nacional. A chave da NF-e/CT-e tem 44 e
  // nao pode ser lida com este offset - fail-closed.
  if (dig.length !== 50) { return ''; }
  return dig.substring(23, 36);
}

// --- DANFSe v2.0 (padrao nacional da NFS-e) ------------------------------
//
// Layout publicado pela NT SE/CGNFS-e e adotado a partir de 2026. Nao eh
// layout de municipio: eh o DANFSe unico que as prefeituras passam a emitir,
// e por isso vem ANTES dos detectores municipais. Sem essa precedencia a
// nota cai no parser do municipio pelo nome da cidade que aparece no
// ENDERECO DO TOMADOR - a NFS-e de Itajai com tomador em Curitiba era lida
// pelo parseCuritiba - e os campos saem errados.
//
// O documento eh COLUNAR: uma linha de rotulos e a linha de baixo com os
// valores alinhados por coluna. Ler "o valor esta na proxima linha", que eh
// o que os parsers municipais fazem, pega o valor da coluna errada:
//
//   BC ISSQN  | Aliquota Aplicada | Retencao do ISSQN | ISSQN Apurado
//   R$ 525,00 | 3,00 %            | Nao Retido        | R$ 15,75
//
// Por isso este parser trabalha sobre o texto extraido POR COLUNAS (itens
// ordenados por y e x, celulas separadas por 3+ espacos - ver
// PdfParserService.extractText) e casa rotulo com valor pelo indice da
// celula.
//
// Armadilha do bloco federal: o v2.0 imprime lado a lado o que foi RETIDO e
// o que eh DEBITO PROPRIO do prestador:
//   "Contribuicoes Sociais - Retidas"  R$ 51,15  <- retencao (entra na nota)
//   "PIS - Debito Apuracao Propria"    R$  7,15  <- NAO eh retencao
// Ler o segundo como retencao gera natureza financeira errada no titulo.

function semAcento(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export function isDanfse2(text: string): boolean {
  const u = semAcento(text.toUpperCase());
  if (u.includes('DANFSE V2.0')) { return true; }
  // Fallback para PDFs que nao imprimem a versao no cabecalho: a combinacao
  // chave da NFS-e + numero da DPS so existe no padrao nacional.
  return u.includes('CHAVE DE ACESSO DA NFS-E') && u.includes('NUMERO DA DPS');
}

// Celulas de uma linha do texto colunar. O extrator marca separacao de
// coluna com 3+ espacos; dentro da celula o espaco simples eh preservado.
function celulas(linha: string): string[] {
  return linha.split(/\s{3,}/).map(c => c.trim()).filter(c => c !== '');
}

interface ParRotVal { rot: string; val: string; }

// Linha que so carrega o titulo da secao. Ela as vezes se mete ENTRE os
// rotulos e os valores, porque o titulo fica numa faixa vertical propria:
//   Codigo de Tributacao Nacional | Codigo da NBS | Local da Prestacao
//   SERVICO PRESTADO
//   10.06.01 / -                  | 1.0607.00.00  | Itajai / SC / -
// Sem reconhecer isso, o par ficaria a duas linhas de distancia e o codigo
// do servico saia vazio (4 notas do acervo).
function ehTituloSecao(linha: string): boolean {
  const cs = celulas(linha);
  if (cs.length !== 1) { return false; }

  const t = cs[0];
  if (t.length > 60 || /\d[.,]\d/.test(t) || /R\$/.test(t)) { return false; }

  const letras = semAcento(t).replace(/[^A-Za-z]/g, '');
  if (letras.length < 3) { return false; }

  const maiusculas = letras.replace(/[^A-Z]/g, '');
  return maiusculas.length / letras.length >= 0.7;
}

// Emparelha cada rotulo com o valor da MESMA coluna na linha seguinte.
//
// Duas formas de alinhamento aparecem no mesmo documento:
//   1) n rotulos / n valores   -> par direto por indice.
//   2) n rotulos / n-1 valores -> a primeira celula eh o TITULO da secao
//      ("VALOR TOTAL DA NFS-e | VALOR DA OPERACAO / SERVICO | ..."), com os
//      valores comecando na segunda coluna. Deslocamos um.
// Qualquer outra combinacao eh ignorada: chutar alinhamento aqui significa
// gravar o valor de um tributo no campo de outro.
function paresRotuloValor(text: string): ParRotVal[] {
  const linhas = text.split('\n');
  const pares: ParRotVal[] = [];

  for (let i = 0; i < linhas.length - 1; i++) {
    const rotulos = celulas(linhas[i]);
    if (rotulos.length === 0) { continue; }

    let valores = celulas(linhas[i + 1]);
    // Titulo de secao entre rotulos e valores: os valores estao uma linha
    // adiante. So pulamos quando a linha do meio nao serve de valor.
    if (valores.length !== rotulos.length && valores.length !== rotulos.length - 1 &&
        ehTituloSecao(linhas[i + 1]) && i + 2 < linhas.length) {
      valores = celulas(linhas[i + 2]);
    }
    if (valores.length === 0) { continue; }

    let base: number;
    if (valores.length === rotulos.length) { base = 0; }
    else if (valores.length === rotulos.length - 1) { base = 1; }
    // Sobra de valor a direita: texto de outra regiao da pagina caiu na mesma
    // faixa vertical (o aviso do QR code entra na linha do numero da DPS e a
    // serie se perdia). Os rotulos continuam sendo a ancora - as celulas a
    // mais, no fim, sao descartadas.
    else if (valores.length > rotulos.length) { base = 0; }
    else { continue; }

    for (let k = base; k < rotulos.length; k++) {
      pares.push({ rot: rotulos[k], val: valores[k - base] ?? '' });
    }
  }

  return pares;
}

// Primeiro valor cujo rotulo casa. A linha de rotulos vem sempre antes da
// linha de valores, entao o primeiro casamento eh o par certo mesmo quando
// o mesmo texto reaparece adiante como valor.
function valorDe(pares: ParRotVal[], re: RegExp): string {
  for (const p of pares) {
    if (re.test(semAcento(p.rot))) { return p.val; }
  }
  return '';
}

function moedaDe(pares: ParRotVal[], re: RegExp): number {
  return parseMoney(valorDe(pares, re));
}

// CNPJ do bloco que comeca no primeiro marcador encontrado. Os marcadores sao
// tentados em ordem de prioridade, nao em alternancia: em PDF com mais de um
// documento (nota de debito + DANFSe) o rotulo mais especifico precisa ganhar
// de um "prestador do servico" solto na primeira pagina.
function cnpjDoBloco(text: string, marcadores: RegExp[]): string {
  const u = semAcento(text);
  for (const marcador of marcadores) {
    const m = u.match(marcador);
    if (!m || m.index === undefined) { continue; }
    const bloco = text.substring(m.index, m.index + 600);
    const cnpj = bloco.match(/\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/);
    if (cnpj) { return cnpj[0].replace(/\D/g, ''); }
    const cru = bloco.match(/(?:^|\s)(\d{14})(?!\d)/);
    if (cru) { return cru[1]; }
  }
  return '';
}

// Rotulos das partes no DANFSe v2.0. O leiaute nacional imprime
// "EMITENTE DA NFS-e / Prestador do Servico" e "TOMADOR DO SERVICO"; as
// variantes "PRESTADOR / FORNECEDOR" e "TOMADOR / ADQUIRENTE" aparecem em
// emissores que seguem o modelo da DPS. Sem o "DO", 198 de 358 notas do
// acervo (09/2026) saiam sem CNPJ do emitente e do tomador - e sem tomador
// a filial nao e reconhecida.
const MARCADORES_EMITENTE = [
  /EMITENTE\s+DA\s+NFS/i,
  /PRESTADOR\s*\/\s*FORNECEDOR/i,
  /PRESTADOR\s+D[EO]\s+SERVI/i
];
const MARCADORES_TOMADOR = [
  /TOMADOR\s*\/\s*ADQUIRENTE/i,
  /TOMADOR\s+D[EO]\s+SERVI/i
];

// Codigo nacional de tributacao ("NN.NN.NN") a partir da celula lida. A celula
// vem como "10.05.01 / -" (nacional / municipal) ou, quando o emissor imprime
// a descricao junto, "33.01.01 - Servicos de desembaraco -".
function codigoNacional(celula: string): string {
  const m = (celula || '').match(/^\s*(\d{2}\.\d{2}\.\d{2})/);
  if (m) { return m[1]; }
  return (celula || '').split('/')[0].trim();
}

// Fallback do codigo nacional quando o pareamento por coluna falha. Com o
// codigo MUNICIPAL vazio a linha de valores tem uma celula a menos que a de
// rotulos, o par desloca e o nacional se perde - 150 notas do acervo
// (09/2026), quase todas de despacho aduaneiro (33.01.01). Ancorado no
// rotulo e exigindo o formato NN.NN.NN no inicio da celula, para nao pegar
// outro numero da pagina.
function codigoNacionalPorLinha(text: string): string {
  const linhas = text.split('\n');
  for (let i = 0; i < linhas.length; i++) {
    if (!/Codigo de Tributacao Nacional/i.test(semAcento(linhas[i]))) { continue; }
    for (let j = i + 1; j <= i + 2 && j < linhas.length; j++) {
      const m = linhas[j].match(/^\s*(\d{2}\.\d{2}\.\d{2})\b/);
      if (m) { return m[1]; }
    }
  }
  return '';
}

// Trecho entre dois marcadores, para limitar a busca a uma secao.
function secao(text: string, de: RegExp, ate: RegExp): string {
  const u = semAcento(text);
  const ini = u.search(de);
  if (ini < 0) { return ''; }
  const resto = u.substring(ini + 1).search(ate);
  const fim = resto < 0 ? text.length : ini + 1 + resto;
  return text.substring(ini, fim);
}

// PIS/COFINS/CSLL retidos vem SOMADOS num campo so ("Contribuicoes Sociais
// - Retidas"); quais tributos entraram na soma esta na descricao ao lado
// ("3 - PIS/COFINS/CSLL Retidos"). Rateamos pelas aliquotas legais, que eh
// como a soma foi montada: PIS 0,65% + COFINS 3% + CSLL 1%.
function rateiaPcc(total: number, descricao: string): { pis: number; cofins: number; csll: number } {
  const zero = { pis: 0, cofins: 0, csll: 0 };
  if (total <= 0) { return zero; }

  const d = semAcento(descricao.toUpperCase());
  // Descricao dizendo que nao houve retencao: o valor ao lado eh de outra
  // coluna. Nao inventamos retencao.
  if (/NAO\s+RETID/.test(d)) { return zero; }

  const temPis    = d === '' || d.includes('PIS');
  const temCofins = d === '' || d.includes('COFINS');
  const temCsll   = d === '' || d.includes('CSLL');

  const aliq = (temPis ? 0.65 : 0) + (temCofins ? 3 : 0) + (temCsll ? 1 : 0);
  if (aliq <= 0) { return zero; }

  return {
    pis:    temPis    ? +(total * 0.65 / aliq).toFixed(2) : 0,
    cofins: temCofins ? +(total * 3.00 / aliq).toFixed(2) : 0,
    csll:   temCsll   ? +(total * 1.00 / aliq).toFixed(2) : 0
  };
}

export function parseDanfse2(text: string): NfseData {
  const pares = paresRotuloValor(text);

  // --- Partes -----------------------------------------------------------
  const cnpjEmissor = cnpjDoBloco(text, MARCADORES_EMITENTE);
  const cnpjTomador = cnpjDoBloco(text, MARCADORES_TOMADOR);

  const blocoPrest  = secao(text, /EMITENTE DA NFS|PRESTADOR\s*\/\s*FORNECEDOR/, /TOMADOR\s*\/\s*ADQUIRENTE|TOMADOR DO SERVI/);
  const nomeEmissor = valorDe(paresRotuloValor(blocoPrest), /^Nome\s*\/\s*Nome\s+Empresarial/i);

  // --- Identificacao ----------------------------------------------------
  const numeroNf = valorDe(pares, /^NUMERO DA NFS-e/i).replace(/\D/g, '');
  const serie    = valorDe(pares, /^SERIE DA DPS/i).replace(/\D/g, '');

  const mChave = text.match(/(?:^|\s)(\d{50})(?!\d)/);
  const chaveAcesso = mChave ? mChave[1] : '';

  // --- Servico ----------------------------------------------------------
  // "10.05.01 / -" = codigo nacional / codigo municipal. O GNF usa o nacional.
  const codTrib = valorDe(pares, /^Codigo de Tributacao Nacional/i);
  const cnaeServico = codigoNacional(codTrib) || codigoNacionalPorLinha(text);

  const blocoDesc = secao(text, /Descricao do Servico/, /TRIBUTACAO MUNICIPAL/);
  const descricaoServico = blocoDesc
    .split('\n').slice(1).map(l => l.trim()).filter(l => l !== '').join(' ').substring(0, 300);

  // Processo: o prestador escreve a referencia na descricao do servico, em
  // formato livre ("Ref. Impo/Expo: ACPR 0280 25", "AC 3876/26 - BRAKO").
  const processo = extractProcesso(blocoDesc) || extractProcesso(text);

  // --- Valores ----------------------------------------------------------
  let valorTotal = moedaDe(pares, /^VALOR DA OPERACAO/i);
  if (valorTotal === 0) { valorTotal = moedaDe(pares, /^BC ISSQN/i); }

  // --- Retencoes --------------------------------------------------------
  // ISS so entra quando o documento diz que foi retido; o valor apurado
  // aparece sempre, retido ou nao.
  const retIss = semAcento(valorDe(pares, /^Retencao do ISSQN/i).toUpperCase());
  const iss = (retIss.includes('RETIDO') && !retIss.includes('NAO RETIDO'))
    ? moedaDe(pares, /^ISSQN Apurado/i)
    : 0;

  // "TRIBUTACAO FEDERAL (EXCETO CBS) IRRF": o titulo da secao e o rotulo do
  // IRRF saem na mesma celula.
  const ir   = moedaDe(pares, /IRRF/i);
  const inss = moedaDe(pares, /^Contribuicao Previdenciaria\s*-?\s*Retida/i);

  const pcc = rateiaPcc(
    moedaDe(pares, /^Contribuicoes Sociais\s*-?\s*Retidas/i),
    valorDe(pares, /^Descricao Contrib\.? Sociais/i)
  );

  // --- Regime e municipio ----------------------------------------------
  const sn = semAcento(valorDe(pares, /^Simples Nacional na Data/i).toUpperCase());
  const simplesNacional = sn.includes('OPTANTE') && !sn.includes('NAO OPTANTE');

  let municipio = '';
  const mMun = semAcento(text).match(/Municipio:\s*([^\n]+?)\s*-\s*[A-Z]{2}\b/i);
  if (mMun) { municipio = mMun[1].trim(); }

  return {
    cnpjEmissor,
    nomeEmissor: nomeEmissor || undefined,
    cnpjTomador,
    numeroNf: numeroNf || undefined,
    serie: serie || undefined,
    chaveAcesso: chaveAcesso || undefined,
    valorTotal,
    cnaeServico: cnaeServico || undefined,
    descricaoServico: descricaoServico || undefined,
    processo,
    retencoes: { ir, pis: pcc.pis, cofins: pcc.cofins, csll: pcc.csll, inss, iss },
    municipio: municipio || undefined,
    simplesNacional,
    source: 'danfse2'
  };
}

// --- Parsers por municipio -----------------------------------------------

function parseCuritiba(text: string): NfseData {
  const cnpj = extractFirstCnpj(text);

  // Nome: tenta com quebra de linha (DANFSe v1.0) e tambem mesma linha
  // (layout transmite/TOTVS). Ambos comecam por "Nome / Nome Empresarial".
  let nome = '';
  const nomeInline = text.match(/Nome\s*\/\s*Nome\s+Empresarial\s+([A-Z][^\n]{4,100})/i);
  if (nomeInline) {
    nome = nomeInline[1].replace(/\s+E-?mail.*$/i, '').trim();
  } else {
    const nomeMatch = text.match(/Nome\s*\/\s*Nome\s*Empresarial[\s\S]*?\n\s*([A-Z][^\n]{4,100})/i);
    if (nomeMatch) nome = nomeMatch[1].trim();
  }

  // Numero da NFS-e. Cobre os sub-layouts de Curitiba:
  //   - DANFSe v1.0 / transmite: "Numero da NFS-e\n<n>"
  //   - Prefeitura Curitiba (estilo boleto/DHL): "Numero Nota Fiscal\n<n>"
  //   - fallback: "RPS: ... / NF <n>" presente em ambos.
  let numero = '';
  let numMatch = text.match(/N[uú]mero\s+da\s+NFS-?e\s*\n?\s*(\d+)/i);
  if (!numMatch) { numMatch = text.match(/N[uú]mero\s+Nota\s+Fiscal\s*\n?\s*(\d+)/i); }
  if (!numMatch) { numMatch = text.match(/\bNF\s+(\d{2,})\b/i); }
  numero = numMatch ? numMatch[1] : '';

  // Valor. Tenta "Valor do Servico R$ X" (DANFSe v1.0); se nao houver,
  // cai pro layout Prefeitura Curitiba: "VALOR DA NOTA = R$ X" e, por
  // ultimo, "VALOR LIQUIDO A PAGAR R$ X".
  let valor = findInlineValue(text, /Valor\s+do\s+Servi[çc]o/i);
  if (!valor) {
    const vNota = text.match(/VALOR\s+DA\s+NOTA\s*=?\s*R?\$?\s*([\d.,]+)/i);
    if (vNota) { valor = parseMoneyBR(vNota[1]); }
  }
  if (!valor) {
    const vLiq = text.match(/VALOR\s+L[IÍ]QUIDO\s+A\s+PAGAR\s*R?\$?\s*([\d.,]+)/i);
    if (vLiq) { valor = parseMoneyBR(vLiq[1]); }
  }

  // CNAE / Codigo de Tributacao Nacional: pode vir como "33.01.01"
  // (DANFSe v1.0) ou "330101" (transmite). Aceita ambos formatos.
  let cnae = '';
  const cnaeDot = text.match(/C[oó]digo\s+de\s+Tributa[çc][aã]o\s+Nacional[\s\S]{0,300}?(\d{2}\.\d{2}\.\d{2})/i);
  if (cnaeDot) {
    cnae = cnaeDot[1];
  } else {
    const cnaeFlat = text.match(/C[oó]digo\s+de\s+Tributa[çc][aã]o\s+Nacional[\s\S]{0,80}?(\d{6})/i);
    if (cnaeFlat) {
      // normaliza "330101" -> "33.01.01" pra casar com a lista de CNAEs do front
      const v = cnaeFlat[1];
      cnae = `${v.substring(0, 2)}.${v.substring(2, 4)}.${v.substring(4, 6)}`;
    }
  }
  // Fallback layout Prefeitura Curitiba (boleto/DHL): nao tem "Codigo de
  // Tributacao Nacional". O codigo municipal de 6 digitos aparece em
  // "Servico Prestado\n10.06 100601" e em "CNAE: 100601". Normaliza o
  // 6-digitos pra "10.06.01" (mesmo formato XX.XX.XX dos outros layouts).
  if (!cnae) {
    let mC = text.match(/Servi[çc]o\s+Prestado\s*\n?\s*(?:\d{1,2}\.\d{2}\s+)?(\d{6})(?!\d)/i);
    if (!mC) { mC = text.match(/CNAE\s*:?\s*(\d{6})(?!\d)/i); }
    if (mC) {
      const v = mC[1];
      cnae = `${v.substring(0, 2)}.${v.substring(2, 4)}.${v.substring(4, 6)}`;
    }
  }

  // Descricao do servico - DANFSe v1.0 usa quebra de linha entre label e valor;
  // transmite/TOTVS coloca tudo na mesma linha.
  let descricao = '';
  const descInline = text.match(/Descri[çc][aã]o\s+do\s+Servi[çc]o\s+([^\n]+)/i);
  if (descInline) {
    descricao = descInline[1].trim();
  } else {
    const descMatch = text.match(/Descri[çc][aã]o\s+do\s+Servi[çc]o\s*\n([^\n]+)/i);
    if (descMatch) descricao = descMatch[1].trim();
  }
  const processo = extractProcesso(descricao);

  // Retencoes federais - regra compartilhada com o parseGeneric.
  const { ir, pis, cofins, csll, inss } = extractRetencoesFederais(text);

  // ISS retido: "Retenção do ISSQN: Não Retido" ou "ISSQN Retido R$ X"
  let iss = 0;
  const issMatch = text.match(/Reten[çc][aã]o\s+do\s+ISSQN\s*\n?\s*([^\n]+)/i);
  if (issMatch && !/n[aã]o\s+retido/i.test(issMatch[1])) {
    const v = issMatch[1].match(/[\d.,]+/);
    iss = v ? parseMoneyBR(v[0]) : 0;
  }
  if (iss === 0) {
    const issqnRet = text.match(/ISSQN\s+Retido\s+R?\$?\s*([\d.,-]+)/i);
    if (issqnRet && !/^-+$/.test(issqnRet[1].trim())) {
      iss = parseMoneyBR(issqnRet[1]);
    }
  }

  return {
    cnpjEmissor: cnpj,
    nomeEmissor: nome,
    cnpjTomador: extractTomadorCnpj(text),
    numeroNf: numero,
    valorTotal: valor,
    cnaeServico: cnae,
    descricaoServico: descricao,
    processo,
    retencoes: { ir, pis, cofins, csll, inss, iss },
    municipio: 'Curitiba',
    source: 'curitiba'
  };
}

function parseTabular(text: string, source: NfseData['source'], municipio: string): NfseData {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  // CNPJ + Nome do emissor agora usam extractEmissor* que distingue
  // prestador vs tomador (layout 2-colunas Publica tem tomador antes).
  const cnpj = extractEmissorCnpj(text);
  let nome = extractEmissorNome(text);
  // Fallback final: procura em linhas iniciais (mantem comportamento antigo)
  if (!nome) {
    for (const line of lines.slice(0, 20)) {
      if (/^[A-Z0-9\s\/\-\.&]+(LTDA|S\/A|S\.A\.?|EIRELI|ME|EPP)/i.test(line)) {
        nome = line.trim();
        break;
      }
    }
  }

  // Numero da NFS-e
  let numero = '';
  const numIdx = lines.findIndex(l => /N[uú]mero\s+da\s+NFS-?e/i.test(l));
  if (numIdx >= 0) {
    // pode estar na propria linha (depois da label) ou na proxima
    for (let j = 0; j <= 3 && numIdx + j < lines.length; j++) {
      const m = lines[numIdx + j].match(/^\s*(\d{3,12})\s*$/);
      if (m && j > 0) { numero = m[1]; break; }
    }
  }
  // Publica (Joinville/Itajai): "00000000027 / A1" - extrai e remove zeros
  if (!numero) {
    const m = text.match(/(\d{6,12})\s*\/\s*[A-Z0-9]+/);
    if (m) numero = String(Number(m[1])); // remove zeros a esquerda
  }
  // Navegantes/Portonave: "Numero NFS-e" (sem "da") com o numero embutido
  // na linha seguinte junto a outros textos. Pega o 1o numero apos o label.
  if (!numero) {
    const m = text.match(/N[uú]mero\s+(?:da\s+)?NFS-?e[\s\S]{0,90}?(\d{4,12})/i);
    if (m) { numero = m[1]; }
  }
  // Ultimo fallback: "Numero RPS: 80424"
  if (!numero) {
    const m = text.match(/N[uú]mero\s+RPS\s*:?\s*(\d{3,12})/i);
    if (m) { numero = m[1]; }
  }

  // Valor total: pega o primeiro "Valor Total" e valor abaixo, ou
  // "VALOR TOTAL DO SERVIÇO: R$ X"
  let valor = 0;
  const valorInline = text.match(/VALOR\s+TOTAL\s+DO\s+SERVI[CÇ]O[:\s]+R?\$?\s*([\d.,]+)/i);
  if (valorInline) {
    valor = parseMoneyBR(valorInline[1]);
  } else {
    valor = findValueAfterLabel(lines, /^Valor\s+Total\s*$/i, 2);
  }

  // CNAE / codigo de servico
  let cnae = '';
  // Publica (Joinville/Itajai): "Codigo do Servico: 17.03 - ..." ou
  // "Codigo do Servico: 11.04.01 Armazenamento..."
  // Em alguns PDFs (NF Itajai 5699) ha linhas entre o label e o codigo:
  //   "Codigo do Servico:"
  //   "VALOR TOTAL DO SERVICO: R$ 2.684,72"
  //   "11.04.01 Armazenamento..."
  // Por isso permite ate 300 chars entre label e codigo, mas restringe o
  // padrao pra (\d{2,4}\.\d{2}(\.\d{2})?) - "11.04.01" / "17.03" - que NAO
  // casa com valores monetarios tipo "2.684,72" (so 1 digito antes do ponto)
  // ou "20.000,00" (precisa virgula no fim - exigido pelo lookahead).
  const cnaePub = text.match(
    /C[oó]digo\s+do\s+Servi[çc]o[\s\S]{0,300}?\b(\d{2,4}\.\d{2}(?:\.\d{2})?)\b(?!\d|,)/i
  );
  if (cnaePub) cnae = cnaePub[1];
  // Tabular: cabecalho "Servico" + valor na linha de baixo
  if (!cnae) {
    const servIdx = lines.findIndex(l => /^Servi[çc]o\s/i.test(l) || /^Servi[çc]o$/i.test(l));
    if (servIdx >= 0 && servIdx + 1 < lines.length) {
      const m = lines[servIdx + 1].match(/^\s*(\d{4,7})\b/);
      if (m) cnae = m[1];
    }
  }

  // Descricao do servico (best-effort)
  let descricao = '';
  const descIdx = lines.findIndex(l => /Descri[çc][aã]o\s+do\s+Servi[çc]o/i.test(l));
  if (descIdx >= 0 && descIdx + 1 < lines.length) {
    descricao = lines[descIdx + 1];
  }
  const processo = extractProcesso(descricao);

  // Retencoes - tabular: header line com IR/INSS/etc, valores na linha de baixo.
  // Para os 3 PDFs tabulares de amostra todas as retencoes sao 0, entao essa
  // heuristica simples (capta o primeiro numero da linha apos a label) cobre
  // os casos esperados. Se algum dia surgir caso com retencao em layout
  // tabular, o usuario pode ajustar manualmente.
  const ir     = findValueAfterLabel(lines, /\bIR\b|\bIRRF\b/, 3);
  const pis    = findValueAfterLabel(lines, /^PIS\b/i, 3);
  const cofins = findValueAfterLabel(lines, /^COFINS\b/i, 3);
  const csll   = findValueAfterLabel(lines, /^CSLL\b/i, 3);
  const inss   = findValueAfterLabel(lines, /^INSS\b/i, 3);
  const iss    = findValueAfterLabel(lines, /Valor\s+ISS\s+retido|ISS\s+Retido/i, 3);

  return {
    cnpjEmissor: cnpj,
    nomeEmissor: nome,
    cnpjTomador: extractTomadorCnpj(text),
    numeroNf: numero,
    valorTotal: valor,
    cnaeServico: cnae,
    descricaoServico: descricao,
    processo,
    retencoes: { ir, pis, cofins, csll, inss, iss },
    municipio,
    source
  };
}

// Layout NFS-e Sao Paulo. Prestador aparece ANTES do tomador no texto
// (layout linear, nao 2-colunas). Retencoes tabulares (IRRF/COFINS/PIS
// na mesma linha com valores na seguinte).
function parseSaoPaulo(text: string): NfseData {
  const cnpj = extractEmissorCnpj(text);
  const nome = extractEmissorNome(text);

  // Numero: "Numero da Nota" + "RPS N. 677, emitido em ..." + "00000677"
  let numero = '';
  const numMatch = text.match(/N[uú]mero\s+da\s+Nota[\s\S]{0,400}?(\d{6,12})/i);
  if (numMatch) numero = String(Number(numMatch[1]));
  if (!numero) {
    // RPS fallback: "RPS Nº 677"
    const rpsMatch = text.match(/RPS\s+N[º°]?\s*(\d+)/i);
    if (rpsMatch) numero = rpsMatch[1];
  }

  // Valor total: "VALOR TOTAL DO SERVICO = R$ 15.982,95"
  let valor = 0;
  const valorInline = text.match(/VALOR\s+TOTAL\s+DO\s+SERVI[CÇ]O\s*[=:]\s*R?\$?\s*([\d.,]+)/i);
  if (valorInline) valor = parseMoney(valorInline[1]);
  if (!valor) {
    valor = findInlineValue(text, /Valor\s+Total|Valor\s+do\s+Servi[çc]o/i);
  }

  // CNAE / Codigo do Servico SP: "03115 - Assessoria ou consultoria..."
  let cnae = '';
  const cnaeMatch = text.match(/C[oó]digo\s+do\s+Servi[çc]o[\s\S]{0,80}?(\d{4,6})\s*-/i);
  if (cnaeMatch) cnae = cnaeMatch[1];

  // Retencoes - layout tabular SP:
  //   "Contrib Previd - Retida (R$) IRRF (R$) COFINS (R$) PIS/PASEP (R$) IPI (R$)"
  //   "0,00 239,74 479,49 103,89 0,00"
  const retencoes: Retencoes = { ...EMPTY_RETENCOES };
  const tabHeaderIdx = text.search(/Contribui[çc][aã]o\s+Previdenci[aá]ria[\s\S]{0,30}IRRF[\s\S]{0,30}COFINS[\s\S]{0,30}PIS/i);
  if (tabHeaderIdx >= 0) {
    // Pega a linha logo apos o header, com as 5 colunas da tabela.
    // Celula SEM valor vem como "-" no DANFSe de SP - tipico do IPI numa
    // nota de servico. A regex antiga exigia 5 numeros, entao a linha
    // inteira era descartada e TODAS as retencoes ficavam 0: a nota entrava
    // com natureza "sem retencao" (253012) e o titulo saia cheio, sem
    // IRRF/PIS/COFINS/CSLL. Caso real - NF 251 e 252 (PAINEL MAXX TAX),
    // linha "0,00 15,86 31,73 6,87 -", natureza devida 253016.
    // Cada coluna aceita numero OU "-"; parseMoney("-") devolve 0, entao
    // coluna vazia continua valendo zero. A 5a coluna (IPI) segue exigida
    // como ancora do layout, mas nao e capturada - nao e retencao.
    const CELULA = '(?:[\\d.,]+|-)';
    const reValores = new RegExp(
      '\\n\\s*(' + CELULA + ')\\s+(' + CELULA + ')\\s+(' + CELULA + ')\\s+(' +
      CELULA + ')\\s+(?:' + CELULA + ')'
    );
    const tail = text.substring(tabHeaderIdx);
    const nextLine = tail.match(reValores);
    if (nextLine) {
      retencoes.inss   = parseMoney(nextLine[1]);
      retencoes.ir     = parseMoney(nextLine[2]);
      retencoes.cofins = parseMoney(nextLine[3]);
      retencoes.pis    = parseMoney(nextLine[4]);
    }
  }
  // CSLL: "Contribuicoes Sociais - Retidas (R$)" - total PCC
  // SP traz so' o total consolidado, nao tem CSLL individualizado.
  // Quando IRRF/COFINS/PIS vieram individuais, deixa CSLL=0.
  if (retencoes.csll === 0 && retencoes.pis > 0 && retencoes.cofins > 0) {
    // Fallback: CSLL = Total PCC - PIS - COFINS
    const pccTotal = findInlineValue(text, /Contribui[çc][oõ]es\s+Sociais\s*-?\s*Retidas/i);
    if (pccTotal > 0) {
      const csll = pccTotal - retencoes.pis - retencoes.cofins;
      if (csll > 0) retencoes.csll = +(csll.toFixed(2));
    }
  }

  // ISS retido: "Valor do ISS (R$) ... 799,14" - mas isso eh o ISS calculado.
  // ISS RETIDO especifico nao tem label clara em SP. Deixa zero.

  return {
    cnpjEmissor: cnpj,
    nomeEmissor: nome,
    cnpjTomador: extractTomadorCnpj(text),
    numeroNf: numero,
    valorTotal: valor,
    cnaeServico: cnae,
    retencoes,
    municipio: 'Sao Paulo',
    source: 'saopaulo'
  };
}

function parseGeneric(text: string): NfseData {
  // Fallback: usa extracao robusta de emissor (corrige caso em que
  // primeiro CNPJ era o tomador em layouts 2-colunas).
  const cnpj = extractEmissorCnpj(text);
  const nome = extractEmissorNome(text);
  const numMatch = text.match(/N[uú]mero[\s\S]{0,30}?(\d{3,12})/i);
  // Valor: tenta "Valor dos Servicos" ANTES de "Valor Total". Nos DANFSe
  // com o bloco IBS/CBS (NT 007/2026) o rotulo "Valor Total" fica na faixa
  // do "B.C. do IBS/CBS", e o findInlineValue devolvia a BASE do IBS/CBS
  // (1.022,20) no lugar do valor do servico (1.076,00) - foi assim que as
  // NFS-e 000003477/000003478 da EXACTA entraram com R$ 53,80 a menos.
  // O regex antigo (/Valor\s+do\s+Servi[çc]o/) tambem nao casava o plural
  // "Valor dos Servicos" usado por esses layouts. Chamado 00017975.
  let valor = findInlineValue(text, /Valor\s+d[oa]s?\s+Servi[çc]os?/i);
  if (!valor) { valor = findInlineValue(text, /Valor\s+Total/i); }

  return {
    cnpjEmissor: cnpj,
    nomeEmissor: nome,
    cnpjTomador: extractTomadorCnpj(text),
    numeroNf: numMatch ? numMatch[1] : '',
    valorTotal: valor,
    // Ate 2026-08-28 aqui ia EMPTY_RETENCOES fixo: qualquer NFS-e de
    // municipio nao mapeado entrava sem retencao. Chamado 00017975.
    retencoes: { ...extractRetencoesFederais(text), iss: 0 },
    source: 'generic'
  };
}

// --- API publica ---------------------------------------------------------

export function parseNfseText(text: string, fileName?: string, textoColunas?: string): NfseData {
  let data: NfseData;

  // DANFSe v2.0 vem antes de qualquer detector de municipio: o layout nacional
  // tem os campos em posicao propria e cai errado em qualquer parser municipal
  // (ver o bloco acima). textoColunas e o texto extraido por coordenadas, que o
  // pareamento rotulo/valor exige; sem ele - chamada antiga, um teste - usamos o
  // texto de fluxo, que resolve a variante de uma coluna por linha.
  if (isDanfse2(text)) {
    data = parseDanfse2(textoColunas && textoColunas.trim() ? textoColunas : text);
    if (!data.processo && fileName) {
      data.processo = extractProcesso(fileName.replace(/(\d{4})\.(\d{2})/g, '$1/$2'));
    }
    return data;
  }

  const municipio = detectMunicipio(text);

  switch (municipio) {
    case 'curitiba':   data = parseCuritiba(text); break;
    case 'navegantes': data = parseTabular(text, 'navegantes', 'Navegantes'); break;
    case 'itapoa':     data = parseTabular(text, 'itapoa', 'Itapoa'); break;
    case 'joinville':  data = parseTabular(text, 'joinville', 'Joinville'); break;
    case 'itajai':     data = parseTabular(text, 'itajai', 'Itajai'); break;
    case 'saopaulo':   data = parseSaoPaulo(text); break;
    default:           data = parseGeneric(text);
  }

  // Numero da NFS-e pela chave de acesso nacional, quando o PDF a imprime.
  // Sobrepoe o que o parser do layout achou: a chave e assinada e carrega o
  // nNFSe em posicao fixa, enquanto o cabecalho de varios municipios so
  // mostra o numero do RPS. Ver extractNumeroChaveNacional. Chamado 00017975.
  const numChave = extractNumeroChaveNacional(text);
  if (numChave) { data.numeroNf = numChave; }

  // Codigo do servico fora do rotulo do layout. O porto de Itajai imprime
  // 'Codigo do Servico: 2001 - Servicos portuarios', que e o codigo MUNICIPAL
  // e nao o da LC 116. Gravamos o que o documento traz: o de-para de servico
  // aceita equivalencia municipal (ZOL tipo 2), e um codigo sem de-para
  // aparece na tela como 'sem cobertura' - acionavel. Vazio nao diz nada.
  if (!data.cnaeServico) {
    const mCod = text.match(/C[o\u00f3]digo\s+d[oe]\s+Servi[c\u00e7]o[:\s]*([0-9][0-9.]{2,10})/i);
    if (mCod) { data.cnaeServico = mCod[1].replace(/\.$/, ''); }
  }

  // Valor total fora do rotulo conhecido do layout. Notas de Joinville e
  // Itajai emitidas por outra plataforma fecham com 'VALOR TOTAL DA NOTA =
  // R$ 1.939,46', rotulo que nenhum parser municipal procura, e a nota entrava
  // com valor zero. So roda quando o parser do layout nao achou nada.
  if (!data.valorTotal) {
    data.valorTotal = findInlineValue(text, /VALOR\s+TOTAL\s+DA\s+NOTA/i);
  }

  // Processo fora do bloco de descricao: cada prestador escreve a referencia
  // onde quer - 'REFERENCIA: AC 2902/25', 'REF. CLIENTE: BR260316 AC 2729/26',
  // '|| Ref Cliente1 AC 304626 ||' - e o recorte de descricao de cada layout
  // nao alcanca todas. Antes de tentar o nome do arquivo, procuramos no texto
  // inteiro: no acervo de PDFs ja entregues pelo portal, 9 notas traziam a
  // referencia impressa e o parser do municipio nao a enxergava.
  if (!data.processo) {
    data.processo = extractProcesso(text);
  }

  // Fallback do processo: tenta extrair do nome do arquivo quando nao
  // achou na descricao. Ex.: "NF ACPR 0026.26.pdf" -> "ACPR 0026/26".
  if (!data.processo && fileName) {
    // normaliza "0026.26" -> "0026/26" para o regex aceitar
    const normalized = fileName.replace(/(\d{4})\.(\d{2})/g, '$1/$2');
    data.processo = extractProcesso(normalized);
  }

  return data;
}

// Soma de retencoes (utilitario para quem chama).
export function sumRetencoes(r: Retencoes): number {
  return r.ir + r.pis + r.cofins + r.csll + r.inss + r.iss;
}
